import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Mountain, Compass, Star, ArrowRight, Plane, Globe, Calendar, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import ShareButton from '../components/ShareButton';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TravelPage = () => {
  const [activeTab, setActiveTab] = useState('domestic');
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleOfflineInterest = async (templeId = null) => {
    if (!user) {
      toast.error('Please login to express interest');
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API}/offline-interest`, { temple_id: templeId }, { withCredentials: true });
      toast.success('Interest recorded! We will contact you soon.');
    } catch (error) {
      console.error('Error recording interest:', error);
      toast.error('Failed to record interest. Please try again.');
    }
  };

  const destinations = {
    domestic: [
      { name: 'Uttarakhand', description: 'Char Dham, Panch Kedar & Nature', featured: true, image: 'https://images.unsplash.com/photo-1577516311194-eb14c570a137' },
      { name: 'Rajasthan', description: 'Royal Heritage & Desert Safari', image: 'https://images.unsplash.com/photo-1599661046289-e31897846e41' },
      { name: 'Kerala', description: 'Backwaters & Ayurveda', image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944' },
      { name: 'Varanasi', description: 'Spiritual Capital of India', image: 'https://images.unsplash.com/photo-1561361058-c24cecae35ca' },
    ],
    international: [
      { name: 'Nepal', description: 'Himalayan Kingdom', image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa' },
      { name: 'Bhutan', description: 'Land of Thunder Dragon', image: 'https://images.unsplash.com/photo-1599661046289-e31897846e41' },
      { name: 'Sri Lanka', description: 'Island of Temples', image: 'https://images.unsplash.com/photo-1561361058-c24cecae35ca' },
      { name: 'Thailand', description: 'Buddhist Heritage', image: 'https://images.unsplash.com/photo-1528181304800-259b08848526' },
    ]
  };

  const experiences = [
    { name: 'Pilgrimage', icon: Star, description: 'Sacred temple tours', color: 'from-[#FF7B00] to-[#D4AF37]' },
    { name: 'Adventure', icon: Mountain, description: 'Trekking & expeditions', color: 'from-emerald-400 to-teal-600' },
    { name: 'Wildlife', icon: Compass, description: 'Nature & safari', color: 'from-amber-500 to-[#FF7B00]' },
    { name: 'Luxury', icon: Globe, description: 'Premium experiences', color: 'from-[#D4AF37] to-yellow-600' },
  ];

  return (
    <div className="bg-[#f8f9fa] min-h-screen">
      <Navbar />

      {/* ═══════════════════ MASSIVE HERO ═══════════════════ */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Parallax Background */}
        <motion.div
          className="absolute inset-0 z-0"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: "easeOut" }}
        >
          <img
            src="https://images.unsplash.com/photo-1649147313351-c86537fda0eb?q=80&w=2000"
            alt="Himalayas Journey"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-[#f8f9fa]"></div>
        </motion.div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto mt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-md mb-6 shadow-lg">
              <Compass className="w-4 h-4 text-[#FF7B00]" />
              <span className="text-[#D4AF37] font-bold text-xs uppercase tracking-[0.2em]">Authentic Pilgrimages</span>
            </div>

            <h1 className="font-heading text-5xl md:text-7xl font-black text-white mb-6 tracking-tight drop-shadow-2xl leading-tight">
              Journey to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF7B00] to-[#D4AF37]">The Divine</span>
            </h1>

            <p className="text-gray-200 text-lg md:text-xl max-w-2xl mx-auto mb-10 font-medium drop-shadow-md">
              Curated spiritual journeys to the most sacred destinations. From Himalayan pilgrimages to temple tours across Asia.
            </p>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <Button className="btn-saffron rounded-full px-8 py-6 text-lg w-full sm:w-auto shadow-[0_8px_32px_rgba(255,123,0,0.4)] hover:-translate-y-1 transition-transform">
                <Star className="mr-2 w-5 h-5 fill-current" />
                Explore Pilgrimages
              </Button>
              <Button className="bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full px-8 py-6 text-lg w-full sm:w-auto hover:bg-white/20 hover:-translate-y-1 transition-all">
                <Plane className="mr-2 w-5 h-5" />
                Custom Plan
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════ EXPERIENCE CATEGORIES ═══════════════════ */}
      <section className="py-24 relative z-20 -mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white/90 backdrop-blur-xl rounded-3xl p-6 md:p-8 text-center cursor-pointer shadow-[0_8px_32px_rgba(0,0,0,0.06)] border border-white hover:shadow-[0_16px_48px_rgba(255,123,0,0.1)] transition-all group"
              >
                <div className={`w-14 h-14 md:w-16 md:h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br ${exp.color} p-[1px] shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <div className="w-full h-full bg-white rounded-[15px] flex items-center justify-center">
                    <exp.icon className="w-7 h-7 md:w-8 md:h-8 text-transparent bg-clip-text bg-gradient-to-br" style={{ backgroundImage: `var(--tw-gradient-stops)` }} stroke="url(#gradient)" />
                  </div>
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-gray-900 mb-2">{exp.name}</h3>
                <p className="text-gray-500 text-xs md:text-sm">{exp.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FEATURED: UTTARAKHAND ═══════════════════ */}
      <section className="py-20 relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-block px-4 py-1.5 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 mb-6">
                <span className="text-[#D4AF37] font-bold text-xs uppercase tracking-[0.2em]">Our Speciality</span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
                Devbhoomi <span className="text-[#FF7B00]">Uttarakhand</span>
              </h2>
              <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                The land of Gods is home to the most sacred Hindu pilgrimage sites. From the majestic Char Dham to the mystical Panch Kedar, experience pure spirituality amidst the sheer grandeur of the Himalayas.
              </p>

              <div className="grid grid-cols-2 gap-y-4 gap-x-2 mb-10">
                {['Char Dham Yatra', 'Panch Kedar Trek', 'Auli Skiing', 'Wildlife Safari'].map((item) => (
                  <div key={item} className="flex items-center space-x-3 group cursor-pointer">
                    <div className="w-8 h-8 rounded-full bg-[#FF7B00]/10 flex items-center justify-center group-hover:bg-[#FF7B00] transition-colors">
                      <Star className="w-4 h-4 text-[#FF7B00] group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-gray-700 font-semibold group-hover:text-gray-900">{item}</span>
                  </div>
                ))}
              </div>

              <Button
                className="btn-saffron rounded-full px-8 py-6 text-lg shadow-[0_8px_24px_rgba(255,123,0,0.3)]"
                onClick={() => handleOfflineInterest()}
                data-testid="interested-offline-puja-btn"
              >
                Interested in offline puja
                <Heart className="ml-2 w-5 h-5 fill-current" />
              </Button>
            </motion.div>

            {/* Right Images Gallery */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1577516311194-eb14c570a137?w=800&q=80"
                  alt="Kedarnath"
                  className="rounded-3xl object-cover h-72 w-full shadow-2xl hover:-translate-y-2 transition-transform duration-500"
                />
                <img
                  src="https://images.unsplash.com/photo-1561361058-c24cecae35ca"
                  alt="Badrinath"
                  className="rounded-3xl object-cover h-72 w-full mt-12 shadow-2xl hover:-translate-y-2 transition-transform duration-500"
                />
              </div>

              {/* Floating Achievement Card */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-xl p-6 rounded-3xl shadow-[0_20px_40px_rgba(0,0,0,0.1)] border border-white text-center w-48">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF7B00] to-[#D4AF37] rounded-full mx-auto flex items-center justify-center mb-3 shadow-inner">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-black text-3xl font-heading text-gray-900 tracking-tight">10K+</h4>
                <p className="text-xs font-bold text-[#FF7B00] uppercase tracking-widest mt-1">Travelers</p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ═══════════════════ DESTINATIONS TABS ═══════════════════ */}
      <section className="py-24 bg-[#FAFBFC] border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header & Tabs */}
          <div className="flex flex-col md:flex-row items-center justify-between mb-16 gap-6">
            <div>
              <h2 className="font-heading text-4xl font-black text-gray-900 tracking-tight">
                Discover <span className="text-[#FF7B00]">Destinations</span>
              </h2>
            </div>

            <div className="flex p-1.5 bg-white rounded-full shadow-sm border border-gray-100">
              <button
                onClick={() => setActiveTab('domestic')}
                className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all ${activeTab === 'domestic' ? 'bg-[#FF7B00] text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
              >
                In India
              </button>
              <button
                onClick={() => setActiveTab('international')}
                className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all ${activeTab === 'international' ? 'bg-[#FF7B00] text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
              >
                International
              </button>
            </div>
          </div>

          {/* Destination Grid */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {destinations[activeTab].map((dest, index) => (
              <motion.div
                key={dest.name}
                whileHover={{ y: -8 }}
                className="group relative rounded-[2rem] overflow-hidden h-96 cursor-pointer shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_40px_rgba(255,123,0,0.15)] transition-all duration-500"
              >
                <img
                  src={dest.image}
                  alt={dest.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                />

                {/* Rich Gradients */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#FF7B00]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {dest.featured && (
                  <div className="absolute top-5 left-5">
                    <span className="px-4 py-1.5 bg-gradient-to-r from-[#FF7B00] to-[#D4AF37] text-white text-xs font-bold rounded-full shadow-lg">
                      Featured
                    </span>
                  </div>
                )}

                <div className="absolute top-5 right-5">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-[#FF7B00] hover:scale-110 transition-all border border-white/30">
                    <ArrowRight className="w-5 h-5 text-white" />
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <div className="flex items-center space-x-1.5 text-white/80 text-xs font-bold tracking-widest uppercase mb-3">
                    <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span>{activeTab === 'domestic' ? 'India' : 'Asia'}</span>
                  </div>
                  <h3 className="font-heading text-3xl font-black text-white mb-2 tracking-tight group-hover:text-[#D4AF37] transition-colors">{dest.name}</h3>
                  <p className="text-white/70 text-sm font-medium line-clamp-2">{dest.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TravelPage;
